import { useEffect, useState, lazy, Suspense } from "react";
import { Routes, Route, Navigate, Outlet, useLocation } from "react-router";
import { ClerkProvider, useAuth, useUser } from "@clerk/clerk-react";
import { ClerkAuthProvider, NoAuthProvider } from "./contexts/PrimateAuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { enUS } from "@clerk/localizations";
import { setTokenGetter, getUsage, getIntercomToken } from "./lib/api";
import { bootIntercom, bootIntercomAnonymous, updateIntercom } from "./lib/intercom";
import { useIsMobile } from "./hooks/useIsMobile";
import { trackPageView } from "./lib/analytics";
// PrimateVision v1 and Landing page are retired — PrimateVisionV2 is now canonical
import PrimateVisionV2 from "./pages/PrimateVisionV2";
import V2Layout from "./components/V2Layout";
import LoginV2 from "./pages/LoginV2";
import SignupV2 from "./pages/SignupV2";
import CookiePolicy from "./pages/CookiePolicy";
import DesignPartnerAgreement from "./pages/DesignPartnerAgreement";
import Waitlist from "./pages/Waitlist";
// import Signup from "./pages/Signup"; // retired — using SignupV2
// import Login from "./pages/Login"; // retired — using LoginV2
import Onboarding from "./pages/Onboarding";
// PRI-438 P9: developer docs replace the PRI-310 stub (lazy — marked + content corpus)
const DocsPage = lazy(() => import("./pages/docs/DocsPage"));
const DocsReference = lazy(() => import("./pages/docs/DocsReference"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const DashboardUsage = lazy(() => import("./pages/DashboardUsage"));
const DashboardApiKeys = lazy(() => import("./pages/DashboardApiKeys"));
const DashboardBilling = lazy(() => import("./pages/DashboardBilling"));
const DashboardSettings = lazy(() => import("./pages/DashboardSettings"));
import Invite from "./pages/Invite";
import AdminWaitlist from "./pages/AdminWaitlist";
import AdminEmailQueue from "./pages/AdminEmailQueue";
import AdminLeads from "./pages/AdminLeads";
import AdminLeadDetail from "./pages/AdminLeadDetail";
import AdminInvestors from "./pages/AdminInvestors";
import AdminInvestorDetail from "./pages/AdminInvestorDetail";
import AdminInvestorCRM from "./pages/AdminInvestorCRM";
import AdminGrowth from "./pages/AdminGrowth";
import AdminBlog from "./pages/Admin";
import AdminBlogDashboard from "./pages/AdminBlog";
import AdminCareers from "./pages/AdminCareers";
import AdminWinLoss from "./pages/AdminWinLoss";
import AdminDevOps from "./pages/AdminDevOps";
import AdminDemoLibrary from "./pages/AdminDemoLibrary";
import AdminAPISettings from "./pages/AdminAPISettings";
import AdminPrompts from "./pages/AdminPrompts";
import AdminPricing from "./pages/AdminPricing";
import AdminDesignPartners from "./pages/AdminDesignPartners";
import PasswordGate from "./components/PasswordGate";
// DashboardLayout retired — all dashboard routes now use V2Layout
// import DashboardLayout from "./components/DashboardLayout";
import NotFound from "./pages/NotFound";
import ServerError from "./pages/ServerError";
import Terms from "./pages/Terms";
import ClaimPage from "./pages/ClaimPage";
import OAuthConsentPage from "./pages/OAuthConsentPage";
import Privacy from "./pages/Privacy";
// PRI-512: About-section content (use-cases, blog, values, team, careers)
// moved to the www marketing site — old app URLs hard-redirect there.
import ExternalRedirect from "./components/ExternalRedirect";

const clerkKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

// Redirects to /login if not signed in.
// Honors the primate-dev-signed-in test bypass (same pattern as
// PrimateAuthContext / PasswordGate) so Playwright can exercise dashboard
// routes without a real Clerk session.
function ProtectedRoute() {
  const { isSignedIn, isLoaded } = useAuth();
  const devSignedIn = typeof localStorage !== 'undefined' && localStorage.getItem('primate-dev-signed-in') === '1';
  if (devSignedIn) return <Outlet />;
  if (!isLoaded) return <div className="min-h-screen bg-void" />;
  if (!isSignedIn) return <Navigate to="/login" replace />;
  return <Outlet />;
}

// Redirects to /dashboard if already signed in
function GuestRoute() {
  const { isSignedIn, isLoaded } = useAuth();
  if (!isLoaded) return <div className="min-h-screen bg-void" />;
  if (isSignedIn) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

// Tracks page views on every route change
function PageViewTracker() {
  const { pathname } = useLocation()
  useEffect(() => {
    trackPageView(pathname)
  }, [pathname])
  return null
}

// Boots Intercom with user context (or anonymously) and updates on route changes
function IntercomManager() {
  const { user, isLoaded } = useUser();
  const location = useLocation();
  const [ready, setReady] = useState(false);
  const [plan, setPlan] = useState<string | undefined>();
  const [jwtToken, setJwtToken] = useState<string | undefined>();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!isLoaded) return;
    if (!user) {
      // No user — boot anonymous immediately
      bootIntercomAnonymous();
      if (isMobile) { updateIntercom({ vertical_padding: 70, hide_default_launcher: true }); }
      setReady(true);
      return;
    }
    // Authenticated user — fetch plan + JWT before booting
    setReady(false);
    Promise.all([
      getUsage().catch(() => null),
      getIntercomToken().catch(() => null),
    ]).then(([usage, intercomData]) => {
      setPlan(usage?.plan ?? undefined);
      setJwtToken(intercomData?.token ?? undefined);
      setReady(true);
    });
  }, [user?.id, isLoaded]);

  useEffect(() => {
    if (!isLoaded || !ready || !user) return;
    bootIntercom({
      userId: user.id,
      email: user.primaryEmailAddress?.emailAddress ?? '',
      name: user.fullName ?? undefined,
      plan,
      createdAt: user.createdAt ? Math.floor(new Date(user.createdAt).getTime() / 1000) : undefined,
      jwtToken,
    });
    if (isMobile) { updateIntercom({ vertical_padding: 110, hide_default_launcher: true }); }
  }, [ready]);

  useEffect(() => {
    if (!isMobile) { updateIntercom(undefined); return; }
    // On dashboard/account pages, push Intercom launcher higher to avoid covering content above the tab bar.
    // Base value 110px accounts for: tab bar (49px) + home indicator safe area (~34px) + margin.
    const isDashboard = location.pathname.startsWith('/dashboard');
    updateIntercom({ vertical_padding: isDashboard ? 130 : 110, hide_default_launcher: true });
  }, [location.pathname, isMobile]);

  return null;
}

// Registers Clerk's getToken with the API client so all requests are authenticated
function ClerkTokenBridge() {
  const { getToken } = useAuth()
  useEffect(() => {
    setTokenGetter(getToken)
  }, [getToken])
  return null
}


function AppRoutesWithAuth() {
  return (
    <ClerkAuthProvider>
    <PasswordGate>
      <Routes>
        {/* ── Public routes — V2 is now canonical ────────────────────────────── */}
        {/* PRI-503: waitlist gate removed — anonymous visitors land directly in
            the product and get a free-credit trial (anon-key.ts, user-intent
            provisioning). /waitlist stays live for email links + the form. */}
        <Route path="/" element={<PrimateVisionV2 />} />
        {/* Legacy v1 paths — redirect to canonical routes */}
        <Route path="/primate-vision" element={<Navigate to="/" replace />} />
        <Route path="/v2" element={<Navigate to="/" replace />} />
        <Route path="/demo" element={<Navigate to="/" replace />} />

        {/* Public content pages (V2 layout) */}
        <Route path="/docs/reference" element={<V2Layout><Suspense fallback={<div style={{ minHeight: '100vh' }} />}><DocsReference /></Suspense></V2Layout>} />
        <Route path="/docs/*" element={<V2Layout><Suspense fallback={<div style={{ minHeight: '100vh' }} />}><DocsPage /></Suspense></V2Layout>} />
        <Route path="/docs" element={<V2Layout><Suspense fallback={<div style={{ minHeight: '100vh' }} />}><DocsPage /></Suspense></V2Layout>} />
        {/* PRI-523: canonical pricing is www /pricing — the app has no pricing page */}
        <Route path="/pricing" element={<ExternalRedirect to="/pricing" />} />
        {/* PRI-512: About-section content lives on www — redirect out of the app */}
        <Route path="/use-cases" element={<ExternalRedirect to="/use-cases" />} />
        <Route path="/blog" element={<ExternalRedirect to="/blog" />} />
        <Route path="/blog/:slug" element={<ExternalRedirect to="/blog/:slug" />} />
        <Route path="/values" element={<ExternalRedirect to="/values" />} />
        <Route path="/team" element={<ExternalRedirect to="/team" />} />
        <Route path="/careers" element={<ExternalRedirect to="/careers" />} />
        <Route path="/invite" element={<V2Layout><Invite /></V2Layout>} />
        <Route path="/waitlist" element={<Waitlist />} />
        <Route path="/terms" element={<V2Layout><Terms /></V2Layout>} />
        <Route path="/privacy" element={<V2Layout><Privacy /></V2Layout>} />
        <Route path="/cookie-policy" element={<V2Layout><CookiePolicy /></V2Layout>} />
        <Route path="/design-partner/:token" element={<V2Layout><DesignPartnerAgreement /></V2Layout>} />
        <Route path="/admin/blog" element={<AdminBlog />} />
        <Route path="/admin/waitlist" element={<AdminWaitlist />} />

        {/* Legacy /v2/* content paths — redirect to canonical */}
        <Route path="/v2/docs" element={<Navigate to="/docs" replace />} />
        <Route path="/v2/pricing" element={<Navigate to="/pricing" replace />} />
        <Route path="/v2/use-cases" element={<Navigate to="/use-cases" replace />} />
        <Route path="/v2/blog" element={<Navigate to="/blog" replace />} />
        <Route path="/v2/blog/:slug" element={<Navigate to="/blog/:slug" replace />} />
        <Route path="/v2/values" element={<Navigate to="/values" replace />} />
        <Route path="/v2/team" element={<Navigate to="/team" replace />} />
        <Route path="/v2/careers" element={<Navigate to="/careers" replace />} />
        <Route path="/v2/terms" element={<Navigate to="/terms" replace />} />
        <Route path="/v2/privacy" element={<Navigate to="/privacy" replace />} />
        <Route path="/v2/cookie-policy" element={<Navigate to="/cookie-policy" replace />} />
        <Route path="/v2/waitlist" element={<Navigate to="/waitlist" replace />} />
        <Route path="/v2/invite" element={<Navigate to="/invite" replace />} />

        {/* Guest-only routes (redirect to /dashboard if signed in) */}
        <Route element={<GuestRoute />}>
          <Route path="/login" element={<LoginV2 />} />
          <Route path="/signup" element={<SignupV2 />} />
          {/* Legacy login/signup aliases — redirect to canonical */}
          <Route path="/sign-in" element={<Navigate to="/login" replace />} />
          <Route path="/sign-up" element={<Navigate to="/signup" replace />} />
          <Route path="/v2/login" element={<Navigate to="/login" replace />} />
          <Route path="/v2/signup" element={<Navigate to="/signup" replace />} />
        </Route>

        {/* Protected routes (redirect to /login if not signed in) */}
        <Route element={<ProtectedRoute />}>
          <Route path="/onboarding" element={<V2Layout><Onboarding /></V2Layout>} />
          <Route path="/v2/onboarding" element={<Navigate to="/onboarding" replace />} />

          {/* Dashboard — V2Layout sidebar everywhere */}
          <Route path="/dashboard" element={<V2Layout><Suspense fallback={<div style={{ minHeight: '100vh' }} />}><Dashboard /></Suspense></V2Layout>} />
          <Route path="/dashboard/usage" element={<V2Layout><Suspense fallback={<div style={{ minHeight: '100vh' }} />}><DashboardUsage /></Suspense></V2Layout>} />
          <Route path="/dashboard/api-keys" element={<V2Layout><Suspense fallback={<div style={{ minHeight: '100vh' }} />}><DashboardApiKeys /></Suspense></V2Layout>} />
          <Route path="/dashboard/billing" element={<V2Layout><Suspense fallback={<div style={{ minHeight: '100vh' }} />}><DashboardBilling /></Suspense></V2Layout>} />
          <Route path="/dashboard/settings" element={<V2Layout><Suspense fallback={<div style={{ minHeight: '100vh' }} />}><DashboardSettings /></Suspense></V2Layout>} />
          <Route path="/dashboard/admin" element={<V2Layout><AdminWaitlist /></V2Layout>} />
          <Route path="/dashboard/admin/emails" element={<V2Layout><AdminEmailQueue /></V2Layout>} />
          <Route path="/dashboard/leads" element={<V2Layout><AdminLeads /></V2Layout>} />
          <Route path="/dashboard/leads/:id" element={<V2Layout><AdminLeadDetail /></V2Layout>} />
          <Route path="/dashboard/investors" element={<V2Layout><AdminInvestors /></V2Layout>} />
          <Route path="/dashboard/investors/:id" element={<V2Layout><AdminInvestorDetail /></V2Layout>} />
          <Route path="/dashboard/investor-crm" element={<V2Layout><AdminInvestorCRM /></V2Layout>} />
          <Route path="/dashboard/growth" element={<V2Layout><AdminGrowth /></V2Layout>} />
          <Route path="/dashboard/blog" element={<V2Layout><AdminBlogDashboard /></V2Layout>} />
          <Route path="/dashboard/careers" element={<V2Layout><AdminCareers /></V2Layout>} />
          <Route path="/dashboard/admin/winloss" element={<V2Layout><AdminWinLoss /></V2Layout>} />
          <Route path="/dashboard/admin/api-settings" element={<V2Layout><AdminAPISettings /></V2Layout>} />
          <Route path="/dashboard/admin/demo-library" element={<V2Layout><AdminDemoLibrary /></V2Layout>} />
          <Route path="/dashboard/admin/devops" element={<V2Layout><AdminDevOps /></V2Layout>} />
          <Route path="/dashboard/admin/prompts" element={<V2Layout><AdminPrompts /></V2Layout>} />
          <Route path="/dashboard/admin/pricing" element={<V2Layout><AdminPricing /></V2Layout>} />
          <Route path="/dashboard/admin/design-partners" element={<V2Layout><AdminDesignPartners /></V2Layout>} />

          {/* Legacy /v2/dashboard/* — redirect to canonical /dashboard/* */}
          <Route path="/v2/dashboard" element={<Navigate to="/dashboard" replace />} />
          <Route path="/v2/dashboard/usage" element={<Navigate to="/dashboard/usage" replace />} />
          <Route path="/v2/dashboard/api-keys" element={<Navigate to="/dashboard/api-keys" replace />} />
          <Route path="/v2/dashboard/billing" element={<Navigate to="/dashboard/billing" replace />} />
          <Route path="/v2/dashboard/settings" element={<Navigate to="/dashboard/settings" replace />} />
          <Route path="/v2/dashboard/admin" element={<Navigate to="/dashboard/admin" replace />} />
          <Route path="/v2/dashboard/admin/emails" element={<Navigate to="/dashboard/admin/emails" replace />} />
          <Route path="/v2/dashboard/leads" element={<Navigate to="/dashboard/leads" replace />} />
          <Route path="/v2/dashboard/leads/:id" element={<Navigate to="/dashboard/leads/:id" replace />} />
          <Route path="/v2/dashboard/investors" element={<Navigate to="/dashboard/investors" replace />} />
          <Route path="/v2/dashboard/investors/:id" element={<Navigate to="/dashboard/investors/:id" replace />} />
          <Route path="/v2/dashboard/investor-crm" element={<Navigate to="/dashboard/investor-crm" replace />} />
          <Route path="/v2/dashboard/growth" element={<Navigate to="/dashboard/growth" replace />} />
          <Route path="/v2/dashboard/blog" element={<Navigate to="/dashboard/blog" replace />} />
          <Route path="/v2/dashboard/careers" element={<Navigate to="/dashboard/careers" replace />} />
          <Route path="/v2/dashboard/admin/winloss" element={<Navigate to="/dashboard/admin/winloss" replace />} />
          <Route path="/v2/dashboard/admin/settings" element={<Navigate to="/dashboard/admin/pricing" replace />} />
          <Route path="/v2/dashboard/admin/api-settings" element={<Navigate to="/dashboard/admin/api-settings" replace />} />
          <Route path="/v2/dashboard/admin/demo-library" element={<Navigate to="/dashboard/admin/demo-library" replace />} />
          <Route path="/v2/dashboard/admin/devops" element={<Navigate to="/dashboard/admin/devops" replace />} />
          <Route path="/v2/dashboard/admin/prompts" element={<Navigate to="/dashboard/admin/prompts" replace />} />
          <Route path="/v2/dashboard/admin/pricing" element={<Navigate to="/dashboard/admin/pricing" replace />} />
        </Route>

        {/* Device-code key claim (PRI-456) — self-authenticated, redirects to /login if needed */}
        <Route path="/claim/:code" element={<ClaimPage />} />
        <Route path="/claim" element={<ClaimPage />} />

        {/* OAuth consent (PRI-462) — self-authenticated, redirects to /login if needed */}
        <Route path="/oauth/consent" element={<OAuthConsentPage />} />

        <Route path="/500" element={<ServerError />} />
        <Route path="*" element={<V2Layout><NotFound /></V2Layout>} />
      </Routes>
    </PasswordGate>
    </ClerkAuthProvider>
  );
}


function AppRoutesNoAuth() {
  return (
    <NoAuthProvider>
    <PasswordGate>
      <Routes>
        {/* V2 is canonical */}
        <Route path="/" element={<PrimateVisionV2 />} />
        <Route path="/primate-vision" element={<Navigate to="/" replace />} />
        <Route path="/v2" element={<Navigate to="/" replace />} />
        <Route path="/demo" element={<Navigate to="/" replace />} />

        {/* Public content pages */}
        <Route path="/docs/reference" element={<V2Layout><Suspense fallback={<div style={{ minHeight: '100vh' }} />}><DocsReference /></Suspense></V2Layout>} />
        <Route path="/docs/*" element={<V2Layout><Suspense fallback={<div style={{ minHeight: '100vh' }} />}><DocsPage /></Suspense></V2Layout>} />
        <Route path="/docs" element={<V2Layout><Suspense fallback={<div style={{ minHeight: '100vh' }} />}><DocsPage /></Suspense></V2Layout>} />
        {/* PRI-523: canonical pricing is www /pricing — the app has no pricing page */}
        <Route path="/pricing" element={<ExternalRedirect to="/pricing" />} />
        {/* PRI-512: About-section content lives on www — redirect out of the app */}
        <Route path="/use-cases" element={<ExternalRedirect to="/use-cases" />} />
        <Route path="/blog" element={<ExternalRedirect to="/blog" />} />
        <Route path="/blog/:slug" element={<ExternalRedirect to="/blog/:slug" />} />
        <Route path="/values" element={<ExternalRedirect to="/values" />} />
        <Route path="/team" element={<ExternalRedirect to="/team" />} />
        <Route path="/careers" element={<ExternalRedirect to="/careers" />} />
        <Route path="/invite" element={<V2Layout><Invite /></V2Layout>} />
        <Route path="/waitlist" element={<Waitlist />} />
        <Route path="/terms" element={<V2Layout><Terms /></V2Layout>} />
        <Route path="/privacy" element={<V2Layout><Privacy /></V2Layout>} />
        <Route path="/cookie-policy" element={<V2Layout><CookiePolicy /></V2Layout>} />
        <Route path="/design-partner/:token" element={<V2Layout><DesignPartnerAgreement /></V2Layout>} />
        <Route path="/admin/waitlist" element={<AdminWaitlist />} />
        <Route path="/login" element={<LoginV2 />} />
        <Route path="/signup" element={<SignupV2 />} />
        <Route path="/sign-in" element={<Navigate to="/login" replace />} />
        <Route path="/sign-up" element={<Navigate to="/signup" replace />} />
        <Route path="/onboarding" element={<V2Layout><Onboarding /></V2Layout>} />

        {/* Legacy /v2/* redirects */}
        <Route path="/v2/docs" element={<Navigate to="/docs" replace />} />
        <Route path="/v2/pricing" element={<Navigate to="/pricing" replace />} />
        <Route path="/v2/use-cases" element={<Navigate to="/use-cases" replace />} />
        <Route path="/v2/blog" element={<Navigate to="/blog" replace />} />
        <Route path="/v2/values" element={<Navigate to="/values" replace />} />
        <Route path="/v2/team" element={<Navigate to="/team" replace />} />
        <Route path="/v2/careers" element={<Navigate to="/careers" replace />} />
        <Route path="/v2/login" element={<Navigate to="/login" replace />} />
        <Route path="/v2/signup" element={<Navigate to="/signup" replace />} />
        <Route path="/v2/terms" element={<Navigate to="/terms" replace />} />
        <Route path="/v2/privacy" element={<Navigate to="/privacy" replace />} />
        <Route path="/v2/cookie-policy" element={<Navigate to="/cookie-policy" replace />} />
        <Route path="/v2/waitlist" element={<Navigate to="/waitlist" replace />} />
        <Route path="/v2/onboarding" element={<Navigate to="/onboarding" replace />} />

        {/* Device-code key claim — ClaimPage uses usePrimateAuth() (context-safe, works here) */}
        <Route path="/claim/:code" element={<ClaimPage />} />
        <Route path="/claim" element={<ClaimPage />} />

        {/* OAuth consent (PRI-462) — context-safe via usePrimateAuth() */}
        <Route path="/oauth/consent" element={<OAuthConsentPage />} />

        <Route path="/500" element={<ServerError />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </PasswordGate>
    </NoAuthProvider>
  );
}

export default function App() {
  if (clerkKey) {
    return (
      <ThemeProvider>
        <ClerkProvider
          publishableKey={clerkKey}
          signInUrl="/login"
          signUpUrl="/signup"
          localization={{
            ...enUS,
            signIn: { start: { title: 'Sign in to Primate Vision', subtitle: '' } },
            signUp: { start: { title: 'Sign up for Primate Vision', subtitle: '' } },
          }}
        >
          <ClerkTokenBridge />
          <PageViewTracker />
          <IntercomManager />
          <AppRoutesWithAuth />
        </ClerkProvider>
      </ThemeProvider>
    );
  }
  return (
    <ThemeProvider>
      <AppRoutesNoAuth />
    </ThemeProvider>
  );
}
